import type { IntlShape } from 'react-intl';

/**
 * Pure formatting helpers for attachment chip captions.
 *
 * Lives outside ``ProjectChatMessage`` so it can be unit-tested
 * without dragging in the entire transcript renderer's dependency
 * tree (Jotai atoms, React Query, the project hooks). The two
 * functions are intentionally tiny — moving them here is about
 * test surface, not abstraction.
 */

/**
 * Format a byte count as a short human-readable string for the
 * file-chip caption row. Mirrors the precision Evidence uses on
 * its ``FileCard``: one decimal under 10 MB, round numbers above.
 *
 *   * ``undefined`` / ``null`` → ``undefined`` (no caption rendered).
 *   * ``< 1 KB`` → ``"<n>B"`` (narrow byte symbol; spacing is locale-set).
 *   * ``1 KB ≤ n < 1 MB`` → ``"<rounded> KB"``.
 *   * ``1 MB ≤ n < 10 MB`` → ``"<n.n> MB"`` (one decimal).
 *   * ``≥ 10 MB`` → ``"<rounded> MB"``.
 */
export const formatBytes = (
  bytes: number | undefined | null,
  intl: IntlShape
): string | undefined => {
  if (bytes == null) return undefined;
  if (bytes < 1024) {
    // CLDR's `short` form for `byte` is the spelled-out word ("512 byte"
    // in en) while kilobyte/megabyte short forms are symbols ("kB"/"MB");
    // `narrow` is where the byte symbol lives ("512B" en, "512 B" de).
    return intl.formatNumber(bytes, {
      style: 'unit',
      unit: 'byte',
      unitDisplay: 'narrow',
      maximumFractionDigits: 0,
    });
  }
  if (bytes < 1024 * 1024) {
    return intl.formatNumber(bytes / 1024, {
      style: 'unit',
      unit: 'kilobyte',
      unitDisplay: 'short',
      maximumFractionDigits: 0,
    });
  }
  const mb = bytes / (1024 * 1024);
  return intl.formatNumber(mb, {
    style: 'unit',
    unit: 'megabyte',
    unitDisplay: 'short',
    minimumFractionDigits: mb < 10 ? 1 : 0,
    maximumFractionDigits: mb < 10 ? 1 : 0,
  });
};

const MEDIA_TYPE_LABELS: Readonly<Record<string, string>> = {
  'application/pdf': 'PDF',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'text/csv': 'CSV',
  'text/plain': 'TXT',
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
};

const labelFromMediaType = (mediaType: string | undefined): string | null => {
  if (!mediaType) return null;
  const normalized = mediaType.split(';', 1)[0]?.trim().toLowerCase();
  if (!normalized) return null;
  return MEDIA_TYPE_LABELS[normalized] ?? null;
};

/**
 * Short uppercase type label for a file chip. A known media type wins,
 * then the filename extension. Missing both becomes ``"FILE"``.
 */
export const formatAttachmentType = (
  filename: string | undefined,
  mediaType?: string
): string => {
  const fromMedia = labelFromMediaType(mediaType);
  if (fromMedia) return fromMedia;
  if (!filename) return 'FILE';
  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1 || lastDot === filename.length - 1) return 'FILE';
  return filename.slice(lastDot + 1).toUpperCase();
};
